import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ScanLine } from 'lucide-react';
import { Dialog } from '@/shared/ui/dialogs/Dialog';
import { Button } from '@/shared/ui/buttons/Button';
import { apiWarehouseScanGet } from '@/entities/warehouse-arrivals';
import { extractErrorMessage } from '@/shared/api/errors';

type Props = {
  open: boolean;
  onClose: () => void;
};

type ScanMessage = { kind: 'error'; text: string };

export const WarehouseArrivalsScanDialog = ({ open, onClose }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [value, setValue] = useState('');
  const [message, setMessage] = useState<ScanMessage | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setValue('');
    setMessage(null);
    inputRef.current?.focus();
  }, [open]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    const code = value.trim();
    if (!code) return;
    setMessage(null);
    try {
      const res = await apiWarehouseScanGet(code);
      if (res.found && res.arrived && res.package_id) {
        navigate(`/warehouse/arrived-packages/${res.package_id}`);
        onClose();
        return;
      }
      if (res.found && !res.arrived) {
        setMessage({ kind: 'error', text: t('warehouseArrivals.scan.notArrived') });
      } else {
        setMessage({ kind: 'error', text: t('warehouseArrivals.scan.notFound') });
      }
      setValue('');
    } catch (err) {
      setMessage({ kind: 'error', text: extractErrorMessage(err) });
    } finally {
      setValue('');
      inputRef.current?.focus();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('warehouseArrivals.scan.title')}
      size="md"
      closeOnBackdrop={false}
      footer={
        <Button variant="outline" type="button" onClick={onClose}>
          {t('warehouseArrivals.scan.close')}
        </Button>
      }
    >
      <style>{`
        @keyframes scanLineMove {
          0% { transform: translateY(-26px); opacity: 0; }
          15% { opacity: 1; }
          85% { opacity: 1; }
          100% { transform: translateY(26px); opacity: 0; }
        }
        .scan-line {
          animation: scanLineMove 1.6s ease-in-out infinite;
        }
      `}</style>

      <form onSubmit={handleSubmit} className="flex flex-col items-center py-6">
        <input
          ref={inputRef}
          autoFocus
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={() => {
            if (open) inputRef.current?.focus();
          }}
          className="sr-only"
          aria-hidden="true"
          tabIndex={-1}
        />

        <div className="relative flex h-24 w-24 items-center justify-center overflow-hidden rounded-2xl bg-blue-50">
          <ScanLine className="h-14 w-14 animate-pulse text-blue-600" strokeWidth={1.5} />
          <span className="scan-line pointer-events-none absolute left-3 right-3 h-0.5 rounded-full bg-blue-500 shadow-[0_0_8px_2px_rgba(59,130,246,0.6)]" />
        </div>

        <p className="mt-4 text-base font-medium text-gray-700">
          {t('warehouseArrivals.scan.scanning')}
        </p>

        {message && <p className="mt-3 text-sm text-red-600">{message.text}</p>}
      </form>
    </Dialog>
  );
};
